import { Table } from '@tanstack/react-table';
import {
  ChevronFirstIcon,
  ChevronLastIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';

import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import { Skeleton } from '@art-of-hospitality-solutions-llc/ui-kit/components/skeleton';

const formatNumberWithCommas = (n: number) =>
  n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

function ResultRange({
  pageIndex,
  pageSize,
  total,
}: {
  pageIndex: number;
  pageSize: number;
  total: number;
}) {
  if (!total) {
    return <span className="font-medium text-zinc-800">0</span>;
  }

  const start = pageIndex * pageSize + 1;
  const end = Math.min((pageIndex + 1) * pageSize, total);

  return (
    <span className="font-medium text-zinc-800">
      {formatNumberWithCommas(start)}–{formatNumberWithCommas(end)}
    </span>
  );
}

interface PermissionsTablePaginationProps<TData> {
  table: Table<TData>;
  total: number;
  isLoading?: boolean;
}

export function PermissionsTablePagination<TData>({
  table,
  total,
  isLoading,
}: PermissionsTablePaginationProps<TData>) {
  const { pageIndex, pageSize } = table.getState().pagination;

  return (
    <div className="sticky bottom-0 z-20 mt-4 flex items-center justify-between border-t border-zinc-300 bg-zinc-100 px-2 py-4">
      {/* left */}
      <div className="flex-1 text-sm text-zinc-500">
        {isLoading ? (
          <Skeleton className="h-4 w-40" />
        ) : (
          <>
            Viewing{' '}
            <ResultRange
              pageIndex={pageIndex}
              pageSize={pageSize}
              total={total}
            />{' '}
            of{' '}
            <span className="font-medium text-zinc-800">
              {formatNumberWithCommas(total)}
            </span>{' '}
            results.
          </>
        )}
      </div>

      {/* right */}
      <div className="flex items-center space-x-6 lg:space-x-8">
        {/* page size */}
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={`${pageSize}`}
            onValueChange={(value) => {
              table.setPageSize(Number(value));
              table.setPageIndex(0);
            }}
          >
            <SelectTrigger className="h-8 w-[76px]" aria-label="Rows per page">
              <SelectValue />
            </SelectTrigger>
            <SelectContent side="top">
              {[10, 25, 50, 100].map((size) => (
                <SelectItem key={size} value={`${size}`}>
                  {size}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* page indicator */}
        <div className="w-fit text-center text-sm text-zinc-500">
          {isLoading ? (
            <Skeleton className="h-4 w-20" />
          ) : (
            <>
              Page{' '}
              <span className="font-medium text-zinc-800">
                {formatNumberWithCommas(pageIndex + 1)}
              </span>{' '}
              of{' '}
              <span className="font-medium text-zinc-800">
                {formatNumberWithCommas(table.getPageCount() || 1)}
              </span>
            </>
          )}
        </div>

        {/* controls */}
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            aria-label="Go to first page"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronFirstIcon className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            aria-label="Go to previous page"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            aria-label="Go to next page"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            className="h-8 w-8 p-0"
            aria-label="Go to last page"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
          >
            <ChevronLastIcon className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
